import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, getFirestore, query, where } from '@firebase/firestore';
import { app } from '../../firebase.js';
import LeftBar from '../../Components/LeftBar';
import Dashnav from '../../Components/Dashnav';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faCircleCheck, faMagnifyingGlass, faUserCheck } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import defaultProfilePicture from '../../Images/defprofile.png';
import {
  filterSchoolFeeIncomeRecords,
  formatKes,
  formatShortDate,
  normalizeAmount,
  toDateValue
} from '../../utils/financeWorkflow';

const NAIROBI_TIME_ZONE = 'Africa/Nairobi';

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const formatDateTime = (value, fallback = '—') => {
  const dateValue = toDateValue(value);
  if (!dateValue) {
    return fallback;
  }

  return dateValue.toLocaleString('en-KE', {
    timeZone: NAIROBI_TIME_ZONE,
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const getPaymentDate = (payment = {}) => {
  return toDateValue(payment.paymentDate || payment.timeCreated || payment.createdAt);
};

const getCourseLabel = (course = {}) => {
  return String(course.mcourseName || course.courseName || course.name || course.title || '').trim();
};

function ActiveStudentsWithClearedFees() {
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [courseFilter, setCourseFilter] = useState('All');
  const [campusFilter, setCampusFilter] = useState('All');
  const [students, setStudents] = useState([]);

  const fetchStudents = useCallback(async () => {
    setRefreshing(true);

    try {
      const [usersSnapshot, incomeSnapshot, coursesSnapshot] = await Promise.all([
        getDocs(query(collection(db, 'users'), where('userStatus', '==', 'Student'))),
        getDocs(collection(db, 'Income')),
        getDocs(collection(db, 'courses'))
      ]);

      const courseMap = {};
      coursesSnapshot.forEach((docItem) => {
        const data = docItem.data();
        const courseLabel = getCourseLabel(data);

        if (!courseLabel) {
          return;
        }

        courseMap[normalizeText(courseLabel)] = {
          label: courseLabel,
          price: normalizeAmount(data.mcoursePrice || data.coursePrice || data.price || data.fee),
          installment: normalizeAmount(data.feeInstallments || data.monthlyFee || data.monthlyInstallment)
        };
      });

      const incomeByStudent = {};
      filterSchoolFeeIncomeRecords(
        incomeSnapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data()
        }))
      ).forEach((payment) => {
        const studentId = payment.studentId || payment.userId;
        if (!studentId) {
          return;
        }

        if (!incomeByStudent[studentId]) {
          incomeByStudent[studentId] = [];
        }

        incomeByStudent[studentId].push({
          amount: normalizeAmount(payment.incomeAmount || payment.amount),
          date: getPaymentDate(payment)
        });
      });

      const studentRows = [];

      usersSnapshot.forEach((docItem) => {
        const data = docItem.data();
        const userType = normalizeText(data.userType);
        const userStatus = normalizeText(data.userStatus);

        if (userType !== 'student' && userStatus !== 'student') {
          return;
        }

        const userId = data.userId || docItem.id;
        const courseName = String(data.course || '').trim();
        const course = courseMap[normalizeText(courseName)];

        if (!userId || !course || course.price <= 0) {
          return;
        }

        const payments = incomeByStudent[userId] || [];
        const totalPaid = payments.reduce((sum, payment) => sum + normalizeAmount(payment.amount), 0);
        const balance = Math.max(course.price - totalPaid, 0);

        if (balance > 0) {
          return;
        }

        const lastPaymentDate = [...payments]
          .map((payment) => payment.date)
          .filter(Boolean)
          .sort((first, second) => second.getTime() - first.getTime())[0] || null;

        const admissionDate = toDateValue(data.admissionDate || data.appDate);

        studentRows.push({
          id: docItem.id,
          userId,
          bio: data.bio || data.displayName || data.name || 'Unnamed student',
          course: course.label || courseName || 'Course not set',
          campus: data.branchName || data.campus || data.selectedCampus || '',
          email: data.userEmail || data.email || '',
          phoneNumber: data.phoneNumber || data.phone || '',
          profilePic: data.profilePic || defaultProfilePicture,
          admissionDate,
          lastPaymentDate,
          courseFee: course.price,
          monthlyInstallment: course.installment,
          totalPaid,
          balance
        });
      });

      studentRows.sort((first, second) => {
        const firstTime = first.lastPaymentDate?.getTime?.() || first.admissionDate?.getTime?.() || 0;
        const secondTime = second.lastPaymentDate?.getTime?.() || second.admissionDate?.getTime?.() || 0;
        if (secondTime !== firstTime) {
          return secondTime - firstTime;
        }

        return String(first.bio || '').localeCompare(String(second.bio || ''));
      });

      setStudents(studentRows);
    } catch (error) {
      console.error('Failed to load active students with cleared fees:', error);
      setStudents([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [db]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const filteredStudents = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return students.filter((student) => {
      const matchesCourse = courseFilter === 'All' || student.course === courseFilter;
      const matchesCampus = campusFilter === 'All' || String(student.campus || '').trim() === campusFilter;

      if (!matchesCourse || !matchesCampus) {
        return false;
      }

      if (!search) {
        return true;
      }

      return [
        student.bio,
        student.course,
        student.campus,
        student.email,
        student.phoneNumber
      ].some((value) => String(value || '').toLowerCase().includes(search));
    });
  }, [campusFilter, courseFilter, searchTerm, students]);

  const uniqueCourses = useMemo(() => {
    return Array.from(new Set(students.map((student) => student.course).filter(Boolean))).sort();
  }, [students]);

  const uniqueCampuses = useMemo(() => {
    return Array.from(new Set(students.map((student) => student.campus).filter(Boolean))).sort();
  }, [students]);

  const totalCollected = useMemo(() => {
    return students.reduce((sum, student) => sum + normalizeAmount(student.totalPaid), 0);
  }, [students]);

  const viewStudent = (userId) => {
    navigate('/view-student', { state: { myData: userId } });
  };

  return (
    <div className="clientsComp applicationsPage">
      <LeftBar />
      <div className="dashMainContent">
        <Dashnav />

        <div className="appsHeader">
          <div>
            <p className="appsKicker">Data Analytics</p>
            <h4>Active Students with Cleared Fees</h4>
            <p className="appsSubtext">
              Currently enrolled students whose tuition balance is fully cleared.
            </p>
          </div>
          <div className="appsHeaderStats">
            <div className="appsStatChip">
              <span>Total</span>
              <strong>{students.length}</strong>
            </div>
            <div className="appsStatChip">
              <span>Showing</span>
              <strong>{filteredStudents.length}</strong>
            </div>
            <div className="appsStatChip">
              <span>Collected</span>
              <strong>{formatKes(totalCollected)}</strong>
            </div>
          </div>
        </div>

        <div className="appsFiltersCard">
          <div className="appsSearch input-group">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </span>
            <Form.Control
              type="text"
              placeholder="Search name, course, campus..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div className="appsFilterGroup">
            <Form.Select className="filterSelect" value={courseFilter} onChange={(event) => setCourseFilter(event.target.value)}>
              <option value="All">All Courses</option>
              {uniqueCourses.map((course) => (
                <option key={course} value={course}>
                  {course}
                </option>
              ))}
            </Form.Select>
            <Form.Select className="filterSelect" value={campusFilter} onChange={(event) => setCampusFilter(event.target.value)}>
              <option value="All">All Campuses</option>
              {uniqueCampuses.map((campus) => (
                <option key={campus} value={campus}>
                  {campus}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        <div className="appsTableCard">
          <div className="appsTableHeader">
            <h6>
              <FontAwesomeIcon icon={faUserCheck} className="me-2" />
              Cleared Students
            </h6>
            <span>{filteredStudents.length} results</span>
          </div>
          <div className="appsTableWrap">
            {loading ? (
              <div className="incomeStateCard">
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading active students with cleared fees...</span>
                </Spinner>
                <p>Loading active students with cleared fees...</p>
              </div>
            ) : (
              <Table hover responsive className="appsTable">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Course</th>
                    <th>Campus</th>
                    <th>Admission Date</th>
                    <th>Course Fee</th>
                    <th>Total Paid</th>
                    <th>Balance</th>
                    <th>Last Payment</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudents.map((student) => (
                    <tr key={student.id}>
                      <td>
                        <div className="appsStudentCell">
                          <div className="appDefProfPic">
                            <img src={student.profilePic || defaultProfilePicture} alt="profile" />
                          </div>
                          <div>
                            <p className="appsStudentName">{student.bio}</p>
                            <span className="appsStudentMeta">{student.email || student.phoneNumber || 'Student profile'}</span>
                          </div>
                        </div>
                      </td>
                      <td>{student.course || '—'}</td>
                      <td>{student.campus || '—'}</td>
                      <td>{formatShortDate(student.admissionDate)}</td>
                      <td>{formatKes(student.courseFee)}</td>
                      <td>{formatKes(student.totalPaid)}</td>
                      <td>{formatKes(student.balance)}</td>
                      <td>{formatDateTime(student.lastPaymentDate, 'No payment recorded')}</td>
                      <td>
                        <Badge bg="light" text="dark" className="cohortStatusBadge cohortStatusBadge--completed">
                          <FontAwesomeIcon icon={faCircleCheck} className="me-1" />
                          Cleared
                        </Badge>
                      </td>
                      <td>
                        <button className="appsViewBtn" onClick={() => viewStudent(student.userId)}>
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                  {filteredStudents.length === 0 && (
                    <tr>
                      <td colSpan="10" className="appsEmptyState">
                        No active students have cleared their fees yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            )}
          </div>
          <div className="d-flex justify-content-end mt-3">
            <Button variant="outline-dark" onClick={fetchStudents} disabled={refreshing || loading}>
              <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ActiveStudentsWithClearedFees;
